import { StrictMode } from 'react'
import App from './App'
import i18n, {
  articlesPath,
  localeFromPath,
  localeMeta,
  locales,
  pathWithoutLocale,
  type Locale,
} from './i18n'
import { articlesCopy } from './articles/copy'
import {
  createArticlesSeoHeadElements,
  createLegalSeoHeadElements,
  createSeoHeadElements,
  legalDocumentSeo,
  localePath,
  type LegalDocumentKind,
} from './seo'

type PrerenderArguments = {
  url: string
}

function releaseServerRendererHandle() {
  // Rolldown's shared React bundle selects the MessageChannel scheduler while
  // prerendering. Unref that build-only port so a completed Vite build exits.
  const nodeProcess = (globalThis as unknown as { process?: {
    _getActiveHandles?: () => Array<{ constructor: { name: string }; unref?: () => void }>
  } }).process
  nodeProcess?._getActiveHandles?.()
    .filter((handle) => handle.constructor.name === 'MessagePort')
    .forEach((handle) => handle.unref?.())
}

export async function prerender({ url }: PrerenderArguments) {
  // Keep the server renderer out of the browser bundle. The prerender plugin
  // executes this dynamic import only during the production build.
  const { renderToString } = await import('react-dom/server')
  const pathname = new URL(url, 'https://sidewisp.com').pathname.replace(/\/+$/, '') || '/'
  const localizedRoute = pathWithoutLocale(pathname).replace(/\/+$/, '') || '/'
  const legalKind: LegalDocumentKind | null = pathname === '/terms'
    ? 'terms'
    : pathname === '/privacy'
      ? 'privacy'
      : null
  const isArticlesPage = localizedRoute === '/articles'
  const locale: Locale = legalKind ? 'en' : localeFromPath(pathname) ?? 'en'
  const resources = await import(`./locales/${locale}.ts`)
  i18n.addResourceBundle(locale, 'translation', resources.default, true, true)
  await i18n.changeLanguage(locale)
  const t = i18n.getFixedT(locale)

  const html = renderToString(
      <StrictMode>
        <App pathname={url} />
      </StrictMode>,
    )
  releaseServerRendererHandle()

  return {
    html,
    links: new Set([
      ...locales.map(localePath),
      ...locales.map(articlesPath),
      '/terms/',
      '/privacy/',
    ]),
    head: {
      lang: localeMeta[locale].htmlLang,
      title: legalKind
        ? legalDocumentSeo[legalKind].title
        : isArticlesPage
          ? articlesCopy[locale].seo.title
          : t('seo.title'),
      elements: new Set(legalKind
        ? createLegalSeoHeadElements(legalKind)
        : isArticlesPage
          ? createArticlesSeoHeadElements(locale)
          : createSeoHeadElements(locale, t)),
    },
  }
}
